import * as React from 'react';
import { useTranslation } from 'react-i18next';
import type { AuthErrorKey } from '@/lib/authErrors';
import { FormError } from '@/components/common/FormError';
import { Button } from '@/components/ui/button';

/** Seconds between two sign-in links to the same address (GoTrue refuses faster ones anyway). */
export const RESEND_COOLDOWN_S = 60;

/**
 * "Send again" for a sign-in link: disabled for 60 s after every successful send, the remaining seconds
 * in the button's own name (so the counter is not announced every second: the region is aria-live off).
 * A failed send shows its `auth.errors.*` copy and the button turns into Retry, enabled at once.
 */
export function SendAgainButton({
  onSend,
  initiallyCooling = true,
  initialError = null,
}: {
  /** Sends the link; resolves to the error key, or null when sent. */
  onSend(): Promise<AuthErrorKey | null>;
  /** True right after a send: the first 60 s start now. */
  initiallyCooling?: boolean;
  /** The failure of the send that led here (the invite page's automatic proof link). */
  initialError?: AuthErrorKey | null;
}) {
  const { t } = useTranslation();
  const [until, setUntil] = React.useState<number | null>(() =>
    initiallyCooling && !initialError ? Date.now() + RESEND_COOLDOWN_S * 1000 : null,
  );
  const [now, setNow] = React.useState(() => Date.now());
  const [error, setError] = React.useState<AuthErrorKey | null>(initialError);
  const [busy, setBusy] = React.useState(false);
  const [sentAgain, setSentAgain] = React.useState(false);

  React.useEffect(() => {
    if (until === null) return;
    const id = window.setInterval(() => {
      const n = Date.now();
      setNow(n);
      if (n >= until) setUntil(null);
    }, 1000);
    return () => window.clearInterval(id);
  }, [until]);

  const remaining = until === null ? 0 : Math.max(0, Math.ceil((until - now) / 1000));

  async function send() {
    setBusy(true);
    setSentAgain(false);
    const failed = await onSend();
    setBusy(false);
    setError(failed);
    if (failed) return;
    const n = Date.now();
    setNow(n);
    setUntil(n + RESEND_COOLDOWN_S * 1000);
    setSentAgain(true);
  }

  const label =
    remaining > 0 ? t('auth.link.sendAgainIn', { seconds: remaining }) : error ? t('common.retry') : t('auth.link.sendAgain');

  return (
    <div className="flex flex-col gap-3">
      <FormError message={error ? t(`auth.errors.${error}`) : null} />
      <div className="flex flex-wrap items-center gap-3" aria-live="off">
        <Button type="button" variant="outline" disabled={busy || remaining > 0} onClick={() => void send()}>
          {busy ? t('auth.link.sending') : label}
        </Button>
        <p role="status" className="text-sm text-ink-muted">
          {sentAgain ? t('auth.link.sentAgain') : ''}
        </p>
      </div>
    </div>
  );
}
